import { computed, runInAction } from 'mobx';
import pluralize from '@kentik/ui-shared/util/pluralize';
import { RUNBOOK_RESOURCE_TYPES } from '@kentik/ui-shared/runbooks/constants';
import $auth from 'app/stores/$auth';
import RunbookCollection from 'app/views/core/runbooks/RunbookCollection';
import api from 'core/util/api';
import { RBACRunbookPermission } from '@kentik/ui-shared/rbac/constants';

/** Matches an AI Advisor session URL, capturing a valid UUIDv7 session ID.
 *  Anchored to only match relative paths or same-origin URLs to prevent spoofing. */
const UUID_V7_PATTERN = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-7[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}';
const AI_ADVISOR_SESSION_RE = new RegExp(`^/v4/core/ai-advisor/(${UUID_V7_PATTERN})/?$`);

class RunbookStore {
  collection = new RunbookCollection();

  /**
   * Extracts the AI Advisor session ID from an href if it is an internal session link.
   *
   * @param {string} href
   * @returns {string|undefined} the session ID, or undefined if href isn't an internal session link
   */
  getAiAdvisorSessionId(href) {
    const match = href?.match(AI_ADVISOR_SESSION_RE);
    return match?.[1];
  }

  @computed
  get isGlobalRunbooksEnabled() {
    return $auth.hasPermission('runbooks.enableGlobal', { overrideForSudo: false });
  }

  @computed
  get canReadRunbooks() {
    return $auth.hasRbacPermissions([RBACRunbookPermission.READ]);
  }

  @computed
  get canCreateRunbooks() {
    return $auth.hasRbacPermissions([RBACRunbookPermission.CREATE]);
  }

  @computed
  get canUpdateRunbooks() {
    return $auth.hasRbacPermissions([RBACRunbookPermission.UPDATE]);
  }

  @computed
  get canDeleteRunbooks() {
    return $auth.hasRbacPermissions([RBACRunbookPermission.DELETE]);
  }

  @computed
  get runbookSelectOptions() {
    return this.collection.models.map((runbook) => ({
      value: runbook.id,
      label: runbook.get('name'),
      description: runbook.get('description'),
      isPreset: runbook.isPreset
    }));
  }

  get alertPolicyTemplateType() {
    return RUNBOOK_RESOURCE_TYPES.ALERT_POLICY_TEMPLATE;
  }

  get alertPolicyType() {
    return RUNBOOK_RESOURCE_TYPES.ALERT_POLICY;
  }

  get policyResourceType() {
    return $auth.isPresetCompany ? this.alertPolicyTemplateType : this.alertPolicyType;
  }

  get baseUrl() {
    return '/v4/settings/kentik-ai/runbooks';
  }

  getResourceTypeLabel(resourceType, count = 1) {
    let baseLabel;

    if (resourceType === this.alertPolicyTemplateType) {
      baseLabel = 'Template';
    } else if (resourceType === this.alertPolicyType) {
      baseLabel = 'Policy';
    } else {
      baseLabel = 'Resource';
    }

    return pluralize(baseLabel, count);
  }

  /**
   * Get runbooks for a policy or template model.
   * - Templates: returns only template-linked runbooks.
   * - Policies: returns both template-linked and policy-linked runbooks, deduplicated by id.
   *
   * @param {Object} policy - Policy or template model object with isTemplate, ruleId, and/or id properties
   * @returns {Array} Array of runbook models
   */
  getRunbooksForPolicy(policy) {
    if (!policy) {
      return [];
    }

    if (policy.isTemplate) {
      return this.getRunbooksForTemplate(policy);
    }

    // note: eventually can remove policyId check once all runbooks are re-saved to use ruleId, but for now support both
    const { ruleId, id: policyId } = policy;

    let policyRunbooks = [];
    if (ruleId) {
      policyRunbooks = this.collection.getRunbooksForResource(this.alertPolicyType, ruleId);
    }
    if (policyRunbooks.length === 0 && policyId && policyId !== ruleId) {
      policyRunbooks = this.collection.getRunbooksForResource(this.alertPolicyType, policyId);
    }

    const templateRunbooks = this.getRunbooksForTemplate(policy);

    const runbookMap = new Map();
    [...templateRunbooks, ...policyRunbooks].forEach((rb) => runbookMap.set(rb.id, rb));
    return Array.from(runbookMap.values());
  }

  /**
   * Get runbooks mapped to a policy template (always uses alert_policy_template resource type).
   *
   * @param {Object} template - Policy template model with ruleId and/or id
   * @returns {Array} Array of runbook models
   */
  getRunbooksForTemplate(template) {
    if (!template) {
      return [];
    }

    const { ruleId, id: templateId } = template;
    let runbooks = [];

    if (ruleId) {
      runbooks = this.collection.getRunbooksForResource(this.alertPolicyTemplateType, ruleId);
    }

    if (runbooks.length === 0 && templateId && templateId !== ruleId) {
      runbooks = this.collection.getRunbooksForResource(this.alertPolicyTemplateType, templateId);
    }

    return runbooks;
  }

  /**
   * Persist runbook-to-resource associations via the API, then patch the
   * in-memory runbook models so callers don't need a full collection refetch.
   *
   * @param {Object} params
   * @param {string} params.resourceType  - e.g. RUNBOOK_RESOURCE_TYPES.ALERT_POLICY
   * @param {string|number} params.resourceId - ruleId or numeric id of the resource
   * @param {Array<string|number>} params.runbookIds - desired set of runbook ids for the resource
   * @returns {Promise}
   */
  updateResourceRunbooks({ resourceType, resourceId, runbookIds }) {
    return api
      .put(`/api/ui/runbook/resource/${resourceType}/${resourceId}`, {
        data: { runbook_ids: runbookIds }
      })
      .then(({ mappings: finalMappings = [] }) => {
        const resourceIdStr = String(resourceId);
        const companyId = String($auth.getActiveUserProperty('company_id'));
        const isOwnedResourceMapping = (m) =>
          m.resource_type === resourceType && m.resource_id === resourceIdStr && String(m.company_id) === companyId;

        runInAction(() => {
          this.collection.models.forEach((runbook) => {
            const base = (runbook.get('mappings') || []).filter((m) => !isOwnedResourceMapping(m));
            const newMapping = finalMappings.find((m) => String(m.runbook_id) === String(runbook.id));
            runbook.safeSet({ mappings: newMapping ? [...base, newMapping] : base });
          });
        });
      });
  }
}

const $runbook = new RunbookStore();
export default $runbook;
